import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Home from "@/pages/Home";
import About from "@/pages/About";
import TheProgram from "@/pages/TheProgram";
import Level1 from "@/pages/Level1";
import Level2 from "@/pages/Level2";
import Level3 from "@/pages/Level3";
import CyberSafety from "@/pages/CyberSafety";
import ForSchools from "@/pages/ForSchools";
import ForParents from "@/pages/ForParents";
import Philosophy from "@/pages/Philosophy";
import Books from "@/pages/Books";
import ChinnuSeries from "@/pages/ChinnuSeries";
import ChinnuProduct from "@/pages/ChinnuProduct";
import ChinnuBundle from "@/pages/ChinnuBundle";
import ChinnuPhysicalCheckout from "@/pages/ChinnuPhysicalCheckout";
import ChinnuOrderConfirmed from "@/pages/ChinnuOrderConfirmed";
import ChinnuOrderTracking from "@/pages/ChinnuOrderTracking";
import OrderDownload from "@/pages/OrderDownload";
import CyberSafetySeries from "@/pages/CyberSafetySeries";
import CyberSafetyProduct from "@/pages/CyberSafetyProduct";
import CyberSafetyBundle from "@/pages/CyberSafetyBundle";
import CyberSafetyOrderDownload from "@/pages/CyberSafetyOrderDownload";
import CyberSafetyUpdates from "@/pages/CyberSafetyUpdates";
import Level3Series from "@/pages/Level3Series";
import Level3Product from "@/pages/Level3Product";
import Level3Bundle from "@/pages/Level3Bundle";
import Level3OrderDownload from "@/pages/Level3OrderDownload";
import Level3Updates from "@/pages/Level3Updates";
import Contact from "@/pages/Contact";
import AdminOrders from "@/pages/AdminOrders";
import AdminBookInterest from "@/pages/AdminBookInterest";
import AdminPhysicalOrders from "@/pages/AdminPhysicalOrders";
import AdminAuthCallback from "@/pages/AdminAuthCallback";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import WhatsAppFAB from "@/components/site/WhatsAppFAB";
import { Toaster } from "sonner";

/** Lenis momentum scrolling — Books storefront routes only. */
function BooksLenis() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!pathname.startsWith("/books")) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__booksLenis = lenis;
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__booksLenis = null;
    };
  }, [pathname]);
  return null;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const lenis = window.__booksLenis;
    if (hash) {
      // wait a tick so the new page renders before scrolling to anchor
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (!el) return;
        if (lenis) lenis.scrollTo(el, { offset: -70 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return;
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS, THIS BREAKS THE AUTH. Detected synchronously during render (not
  // useEffect) to avoid a race with normal routing on the /admin return trip.
  if (isAdminRoute && location.hash?.includes("session_id=")) {
    return <AdminAuthCallback />;
  }

  return (
    <>
      <BooksLenis />
      <ScrollManager />
      {!isAdminRoute && <SiteHeader />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/the-program" element={<TheProgram />} />
        <Route path="/level-1" element={<Level1 />} />
        <Route path="/level-2" element={<Level2 />} />
        <Route path="/level-3" element={<Level3 />} />
        <Route path="/cyber-safety" element={<CyberSafety />} />
        <Route path="/for-schools" element={<ForSchools />} />
        <Route path="/for-parents" element={<ForParents />} />
        <Route path="/philosophy" element={<Philosophy />} />
        <Route path="/books" element={<Books />} />
        <Route path="/books/chinnu" element={<ChinnuSeries />} />
        <Route path="/books/chinnu/complete-collection" element={<ChinnuBundle />} />
        <Route path="/books/chinnu/checkout/:printSlug" element={<ChinnuPhysicalCheckout />} />
        <Route path="/books/chinnu/order-confirmed/:orderRef" element={<ChinnuOrderConfirmed />} />
        <Route path="/books/chinnu/track-order" element={<ChinnuOrderTracking />} />
        <Route path="/books/chinnu/order/:slug" element={<OrderDownload />} />
        <Route path="/books/chinnu/:bookSlug" element={<ChinnuProduct />} />
        <Route path="/books/cyber-safety" element={<CyberSafetySeries />} />
        <Route path="/books/cyber-safety/updates" element={<CyberSafetyUpdates />} />
        <Route path="/books/cyber-safety/complete-series" element={<CyberSafetyBundle />} />
        <Route path="/books/cyber-safety/order/:slug" element={<CyberSafetyOrderDownload />} />
        <Route path="/books/cyber-safety/:bookSlug" element={<CyberSafetyProduct />} />
        <Route path="/books/level3" element={<Level3Series />} />
        <Route path="/books/level3/updates" element={<Level3Updates />} />
        <Route path="/books/level3/complete-series" element={<Level3Bundle />} />
        <Route path="/books/level3/order/:slug" element={<Level3OrderDownload />} />
        <Route path="/books/level3/:bookSlug" element={<Level3Product />} />
        {/* Printed-in-the-books URLs (lifeiq.in/level3...) — /level-3 (hyphenated)
            remains the existing programme page and is untouched. */}
        <Route path="/level3" element={<Navigate to="/books/level3" replace />} />
        <Route path="/level3/book2-sources" element={<Navigate to="/books/level3/updates#book-2" replace />} />
        <Route path="/level3/book3-sources" element={<Navigate to="/books/level3/updates#book-3" replace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin" element={<AdminOrders />} />
        <Route path="/admin/book-interest" element={<AdminBookInterest />} />
        <Route path="/admin/physical-orders" element={<AdminPhysicalOrders />} />
        <Route path="*" element={<Home />} />
      </Routes>
      {!isAdminRoute && <SiteFooter />}
      {!isAdminRoute && <WhatsAppFAB />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
